import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Container,
  LinearProgress,
  IconButton,
  Grid,
  Paper,
  Stack,
  Typography,
  Box,
  Fade,
  Grow,
  alpha,
  useTheme,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  TextField,
  Skeleton,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import SpeakerImage from '../../media/Lamp Poses-01-1.png';
import { useApi } from '../../hooks/useApi';
import AccountProfileEditor from './AccountProfileEditor';
import { AuthContext } from '../../AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CreateAudit from './CreateAudit';
import Audit from './Audit';

function Index() {
  const jApi = useApi();
  const { user, getUser } = useContext(AuthContext);
  const theme = useTheme();
  const [showCreateAudit, setShowCreateAudit] = useState(false);
  const [showAuditDoc, setShowAuditDoc] = useState(false);
  const [auditDocPath, setAuditDocPath] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [audits, setAudits] = useState([]);
  const [auditSummaries, setAuditSummaries] = useState([]);
  const [selectedAuditRunId, setSelectedAuditRunId] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Blade / flyout state
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedAuditDetails, setSelectedAuditDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isRunningAudit, setIsRunningAudit] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [auditorOverride, setAuditorOverride] = useState('');
  const [isSavingOverride, setIsSavingOverride] = useState(false);
  const bladeOpen = !!selectedRow;

  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('section');

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
      opacity: 0.5,
    },
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedRows = useMemo(() => {
    if (!audits || audits.length === 0) return [];

    return [...audits].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) {
        return order === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [audits, order, orderBy]);

  const extractCodeFromLabel = (label) => {
    const match = label.match(/\((.*?)\)/);
    return match ? match[1] : null;
  };

  const handleAccountChange = (event, newValue) => {
    if (!newValue) return;
    setSelectedAccount(newValue);
    const accountCode = extractCodeFromLabel(newValue);
    sessionStorage.setItem('account', accountCode);
    // Clear drill-down and refresh summary data
    setSelectedAuditRunId(null);
    fetchSummaryData(accountCode);
  };

  const fetchAccounts = async () => {
    try {
      const response = await jApi.request('GET', 'cma/account');
      const options = response.data
        .map(item => `${item.name} (${item.code})`)
        .sort((a, b) => a.localeCompare(b));

      setAccounts(options);

      const storedAccount = sessionStorage.getItem('account');
      const selectedItem = options.find(option => option.includes("(" + storedAccount + ")"));

      if (selectedItem) {
        setSelectedAccount(selectedItem);
      } else if (options.length > 0) {
        setSelectedAccount(options[0]);
        sessionStorage.setItem('account', extractCodeFromLabel(options[0]));
      }
    } catch (error) {
      console.error('Failed to load accounts:', error);
      setErrorMessage('Failed to load accounts: ' + error.message);
    }
  };

  const fetchSummaryData = async (accountCode) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      // Fetch audit summaries
      const url = accountCode ? `cma/audit/summary?account=${accountCode}` : 'cma/audit/summary';
      const response = await jApi.request('GET', url);

      setAuditSummaries(response.data || []);
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to load audit summaries');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAuditData = async (accountCode, auditRunId) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      // Fetch audit records, filtered by account and/or auditRunId
      let url = 'cma/audit?';
      const params = [];
      if (accountCode) params.push(`account=${accountCode}`);
      if (auditRunId) params.push(`auditRunId=${auditRunId}`);
      url += params.join('&');

      const response = await jApi.request('GET', url);

      setAudits(response.data || []);
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to load audit records');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async () => {
    await fetchAccounts();
    const accountCode = sessionStorage.getItem('account');
    if (selectedAuditRunId) {
      await fetchAuditData(accountCode, selectedAuditRunId);
    } else {
      await fetchSummaryData(accountCode);
    }
  };

  const handleTileClick = (auditRunId) => {
    setSelectedAuditRunId(auditRunId);
    const accountCode = selectedAccount ? extractCodeFromLabel(selectedAccount) : null;
    fetchAuditData(accountCode, auditRunId);
  };

  const handleBackToSummary = () => {
    setSelectedAuditRunId(null);
    setSelectedRow(null);
    setSelectedAuditDetails(null);
    const accountCode = selectedAccount ? extractCodeFromLabel(selectedAccount) : null;
    fetchSummaryData(accountCode);
  };

  const fetchAuditDetails = async (auditId) => {
    try {
      setIsLoadingDetails(true);
      const response = await jApi.request('GET', `cma/audit?id=${auditId}`);
      console.log('Audit details response:', response);

      // The response should be an array with one item
      const auditData = Array.isArray(response.data) && response.data.length > 0
        ? response.data[0]
        : response.data;

      console.log('Audit details data:', auditData);
      setSelectedAuditDetails(auditData);
    } catch (error) {
      console.error('Failed to load audit details:', error);
      setErrorMessage('Failed to load audit details: ' + error.message);
      setSelectedAuditDetails(null);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const runAudit = async () => {
    if (!selectedAuditDetails) return;

    try {
      setIsRunningAudit(true);
      setErrorMessage(null);

      // Create VES job
      const vesJobPayload = {
        functionpath: selectedAuditDetails.functionPath,
        splat: {
          account: selectedAuditDetails.account,
          auditId: selectedAuditDetails.id,
        },
      };

      console.log('Creating VES job:', vesJobPayload);
      const vesResponse = await jApi.request('POST', 'ves/job', vesJobPayload);
      console.log('VES job created:', vesResponse);

      const jobId = vesResponse.data?.id;

      if (jobId) {
        // Update audit record with jobId
        const updatePayload = {
          id: selectedAuditDetails.id,
          jobId: jobId,
        };

        console.log('Updating audit record:', updatePayload);
        const updateResponse = await jApi.request('PUT', 'cma/audit', updatePayload);
        console.log('Audit record updated:', updateResponse);

        // Update local state
        setSelectedAuditDetails({
          ...selectedAuditDetails,
          jobId: jobId,
        });

        setSnackbarMessage(`Audit queued successfully. Job ID: ${jobId}`);
        setSnackbarOpen(true);

        // Refresh the audit details
        fetchAuditDetails(selectedAuditDetails.id);
      } else {
        throw new Error('No job ID returned from VES');
      }
    } catch (error) {
      console.error('Failed to run audit:', error);
      setErrorMessage('Failed to run audit: ' + error.message);
      setSnackbarMessage('Failed to queue audit: ' + error.message);
      setSnackbarOpen(true);
    } finally {
      setIsRunningAudit(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setSelectedAuditDetails(null);
    setAuditorOverride('');
    if (row && row.id) {
      fetchAuditDetails(row.id);
    }
  };

  const handleSaveOverride = async () => {
    if (!selectedAuditDetails) return;

    try {
      setIsSavingOverride(true);
      setErrorMessage(null);

      const updatePayload = {
        id: selectedAuditDetails.id,
        userPrompt: auditorOverride,
      };

      console.log('Saving auditor override:', updatePayload);
      await jApi.request('PUT', 'cma/audit', updatePayload);

      setSnackbarMessage('Auditor override saved successfully');
      setSnackbarOpen(true);

      // Refresh audit details to get updated data
      fetchAuditDetails(selectedAuditDetails.id);
    } catch (error) {
      console.error('Failed to save override:', error);
      setErrorMessage('Failed to save override: ' + error.message);
      setSnackbarMessage('Failed to save override');
      setSnackbarOpen(true);
    } finally {
      setIsSavingOverride(false);
    }
  };

  const handleCloseBlade = () => {
    setSelectedRow(null);
    setSelectedAuditDetails(null);
  };

  const selectedIndex = useMemo(() => {
    if (!selectedRow) return -1;
    return sortedRows.findIndex(row => row.id === selectedRow.id);
  }, [selectedRow, sortedRows]);

  const totalCount = sortedRows.length;

  const goPrev = () => {
    if (selectedIndex > 0) {
      const prevRow = sortedRows[selectedIndex - 1];
      setSelectedRow(prevRow);
      setSelectedAuditDetails(null);
      fetchAuditDetails(prevRow.id);
    }
  };

  const goNext = () => {
    if (selectedIndex < totalCount - 1) {
      const nextRow = sortedRows[selectedIndex + 1];
      setSelectedRow(nextRow);
      setSelectedAuditDetails(null);
      fetchAuditDetails(nextRow.id);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  const getStatusChip = (answer) => {
    if (!answer) {
      return (
        <Chip
          label="Pending"
          size="small"
          sx={{
            borderRadius: 1,
            bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
            color: theme.palette.info.main,
            border: `1.5px solid ${alpha(theme.palette.info.main, 0.4)}`,
          }}
        />
      );
    }

    const answerLower = answer.toLowerCase();

    if (answerLower === 'pass') {
      return (
        <Chip
          icon={<CheckCircleIcon fontSize="small" />}
          label="Pass"
          size="small"
          sx={{
            borderRadius: 1,
            bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
            color: theme.palette.success.main,
            '& .MuiChip-icon': { color: theme.palette.success.main },
            border: `1.5px solid ${alpha(theme.palette.success.main, 0.4)}`,
          }}
        />
      );
    } else if (answerLower === 'fail') {
      return (
        <Chip
          icon={<ErrorIcon fontSize="small" />}
          label="Fail"
          size="small"
          sx={{
            borderRadius: 1,
            bgcolor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
            color: theme.palette.error.main,
            '& .MuiChip-icon': { color: theme.palette.error.main },
            border: `1.5px solid ${alpha(theme.palette.error.main, 0.4)}`,
          }}
        />
      );
    } else {
      return (
        <Chip
          icon={<WarningIcon fontSize="small" />}
          label={answer}
          size="small"
          sx={{
            borderRadius: 1,
            bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
            color: theme.palette.warning.main,
            '& .MuiChip-icon': { color: theme.palette.warning.main },
            border: `1.5px solid ${alpha(theme.palette.warning.main, 0.4)}`,
          }}
        />
      );
    }
  };

  if (showCreateAudit) {
    return (
      <CreateAudit
        selectedAccount={selectedAccount ? extractCodeFromLabel(selectedAccount) : null}
        onClose={() => {
          setShowCreateAudit(false);
          fetchData();
        }}
      />
    );
  }

  if (showAuditDoc) {
    return (
      <Audit
        filePath={auditDocPath}
        onClose={() => {
          setShowAuditDoc(false);
          setAuditDocPath(null);
        }}
      />
    );
  }

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: alpha(theme.palette.background.paper, 0.8),
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.secondary.main, 0.1),
                    },
                  }}
                >
                  <HomeRoundedIcon
                    sx={{
                      fontSize: 20,
                      color: theme.palette.secondary.main,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.secondary.main,
                      fontWeight: 600,
                    }}
                  >
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                /
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  px: 1,
                  py: 0.5,
                }}
              >
                Audit Center
              </Typography>
            </Box>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          {/* Header */}
          <Grid size={12}>
            <Grow in timeout={700}>
              <Box sx={headerGradient}>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{
                    p: 3,
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.2)}`,
                      border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                    }}
                  >
                    <img width="100%" height="100%" src={SpeakerImage} alt="Logo" style={{ objectFit: 'cover' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 0.5,
                      }}
                    >
                      Audit Center
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Review audit findings and compliance checks.
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 450, mr: 2 }}>
                    {isLoading && !accounts ? (
                      <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 2 }} />
                    ) : accounts && accounts.length > 0 ? (
                      <Autocomplete
                        options={accounts}
                        value={selectedAccount || null}
                        renderInput={(params) => (
                          <TextField {...params} label="Select Account" variant="outlined" size="small" />
                        )}
                        onChange={handleAccountChange}
                        isOptionEqualToValue={(option, value) => option === value}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: alpha(theme.palette.background.paper, 0.8),
                            backdropFilter: 'blur(10px)',
                          },
                        }}
                      />
                    ) : null}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setShowCreateAudit(true)}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                        },
                      }}
                    >
                      New Audit
                    </Button>
                    <IconButton
                      onClick={fetchData}
                      disabled={isLoading}
                      sx={{
                        background: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                      title="Refresh"
                    >
                      <RefreshIcon />
                    </IconButton>
                    {isLoading && <CircularProgress size={24} />}
                  </Stack>
                </Stack>
                {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
              </Box>
            </Grow>
          </Grid>

          {/* Summary Tiles or Detail View */}
          {!selectedAuditRunId ? (
            // Summary Tiles View
            <Grid size={12}>
              <Grow in timeout={900}>
                <Box>
                  {!isLoading && auditSummaries.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                      <Typography variant="h6" color="text.secondary">
                        No audits found.
                      </Typography>
                    </Paper>
                  ) : (
                    <Grid container spacing={3}>
                      {auditSummaries.map((summary, index) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={summary.auditRunId}>
                          <Grow in timeout={1000 + index * 100}>
                            <Paper
                              onClick={() => handleTileClick(summary.auditRunId)}
                              sx={{
                                p: 3,
                                cursor: 'pointer',
                                background: alpha(theme.palette.background.paper, 0.95),
                                backdropFilter: 'blur(20px)',
                                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                borderRadius: 3,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                                  borderColor: alpha(theme.palette.primary.main, 0.5),
                                },
                              }}
                            >
                              <Stack spacing={2}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Box
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      borderRadius: 2,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
                                    }}
                                  >
                                    <AssessmentIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="h6" fontWeight="bold" noWrap>
                                      {summary.auditRunId}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {summary.auditDate || 'No date'}
                                    </Typography>
                                  </Box>
                                </Stack>

                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  {summary.passCount > 0 && (
                                    <Chip
                                      icon={<CheckCircleIcon fontSize="small" />}
                                      label={`${summary.passCount} Pass`}
                                      size="small"
                                      sx={{
                                        bgcolor: alpha(theme.palette.success.main, 0.1),
                                        color: 'success.main',
                                        '& .MuiChip-icon': { color: 'success.main' },
                                      }}
                                    />
                                  )}
                                  {summary.failCount > 0 && (
                                    <Chip
                                      icon={<ErrorIcon fontSize="small" />}
                                      label={`${summary.failCount} Fail`}
                                      size="small"
                                      sx={{
                                        bgcolor: alpha(theme.palette.error.main, 0.1),
                                        color: 'error.main',
                                        '& .MuiChip-icon': { color: 'error.main' },
                                      }}
                                    />
                                  )}
                                  {summary.warningCount > 0 && (
                                    <Chip
                                      icon={<WarningIcon fontSize="small" />}
                                      label={`${summary.warningCount} Warning`}
                                      size="small"
                                      sx={{
                                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                                        color: 'warning.main',
                                        '& .MuiChip-icon': { color: 'warning.main' },
                                      }}
                                    />
                                  )}
                                </Stack>

                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Total Items: <strong>{summary.totalItems}</strong>
                                  </Typography>
                                  {summary.auditType && (
                                    <Typography variant="body2" color="text.secondary">
                                      Type: {summary.auditType}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </Paper>
                          </Grow>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Grow>
            </Grid>
          ) : (
            // Detail Table View
            <Grid size={12}>
              <Grow in timeout={900}>
                <Paper
                  sx={{
                    background: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.8)
                      : alpha(theme.palette.background.paper, 0.95),
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                    borderRadius: 3,
                    boxShadow: theme.palette.mode === 'dark'
                      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                    overflow: 'hidden',
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      bgcolor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.4)
                        : alpha(theme.palette.background.default, 0.6),
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ p: 3, pb: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <IconButton
                          onClick={handleBackToSummary}
                          sx={{
                            background: alpha(theme.palette.primary.main, 0.1),
                            '&:hover': {
                              background: alpha(theme.palette.primary.main, 0.2),
                            },
                          }}
                        >
                          <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight="bold">
                          Audit: {selectedAuditRunId}
                        </Typography>
                      </Stack>
                    </Box>

                  <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow
                          sx={{
                            bgcolor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.background.paper, 0.5)
                              : alpha(theme.palette.background.paper, 0.8),
                          }}
                        >
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'section'}
                              direction={orderBy === 'section' ? order : 'asc'}
                              onClick={() => handleRequestSort('section')}
                            >
                              Section
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'title'}
                              direction={orderBy === 'title' ? order : 'asc'}
                              onClick={() => handleRequestSort('title')}
                            >
                              Title
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'question'}
                              direction={orderBy === 'question' ? order : 'asc'}
                              onClick={() => handleRequestSort('question')}
                            >
                              Question
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center">
                            <TableSortLabel
                              active={orderBy === 'answer'}
                              direction={orderBy === 'answer' ? order : 'asc'}
                              onClick={() => handleRequestSort('answer')}
                            >
                              Answer
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="center">Override</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {sortedRows.map((row) => (
                          <TableRow
                            key={row.id}
                            onClick={() => handleRowClick(row)}
                            sx={{
                              cursor: 'pointer',
                              transition: 'background-color 0.15s ease',
                              '&:nth-of-type(odd)': {
                                bgcolor: theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.background.paper, 0.3)
                                  : alpha(theme.palette.background.paper, 0.9),
                              },
                              '&:nth-of-type(even)': {
                                bgcolor: theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.background.paper, 0.2)
                                  : alpha(theme.palette.background.paper, 0.6),
                              },
                              '&:last-child td, &:last-child th': { border: 0 },
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? `${alpha(theme.palette.primary.main, 0.15)} !important`
                                  : `${alpha(theme.palette.primary.main, 0.08)} !important`,
                              },
                            }}
                          >
                            <TableCell>
                              <Chip label={row.section || '-'} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'medium' }}>{row.title || '-'}</TableCell>
                            <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.question || '-'}
                            </TableCell>
                            <TableCell align="center">{getStatusChip(row.answer)}</TableCell>
                            <TableCell align="center">
                              {row.userPrompt && (
                                <EditIcon
                                  sx={{
                                    fontSize: 20,
                                    color: theme.palette.info.main,
                                  }}
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {audits.length === 0 && !isLoading && (
                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography>No audit records found.</Typography>
                    </Box>
                  )}
                </Paper>
              </Paper>
            </Grow>
          </Grid>
          )}
        </Grid>

        {errorMessage && (
          <Grid size={12} sx={{ pb: 1, pt: 1 }}>
            <Typography variant="body1" color="error">
              {errorMessage}
            </Typography>
          </Grid>
        )}
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />

      {/* Right-side Blade */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: bladeOpen ? 0 : '-820px',
          width: { xs: '95vw', md: '820px' },
          height: '100%',
          bgcolor: 'background.paper',
          boxShadow: '-12px 0 32px rgba(0,0,0,0.3)',
          transition: 'right 0.35s ease-in-out',
          zIndex: 1300,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {bladeOpen && selectedRow && (
          <Box sx={{ p: 4 }}>
            <Box sx={{ mb: 3 }}>
              {/* Top row: nav on left, close on right */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                {/* Navigation */}
                {selectedIndex >= 0 && totalCount > 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, userSelect: 'none' }}>
                    <IconButton
                      size="small"
                      onClick={goPrev}
                      disabled={selectedIndex <= 0}
                      sx={{
                        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                        borderRadius: 2,
                        width: 36,
                        height: 36,
                      }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>

                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        letterSpacing: '0.4px',
                        color: 'text.secondary',
                        minWidth: 90,
                        textAlign: 'center',
                      }}
                    >
                      {selectedIndex + 1}/{totalCount}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={goNext}
                      disabled={selectedIndex >= totalCount - 1}
                      sx={{
                        border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                        borderRadius: 2,
                        width: 36,
                        height: 36,
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box />
                )}

                {/* Close */}
                <IconButton
                  onClick={handleCloseBlade}
                  size="large"
                  aria-label="close"
                  sx={{ color: 'text.secondary' }}
                >
                  <CloseIcon fontSize="large" />
                </IconButton>
              </Box>

              {/* Title row */}
              <Typography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.25, mb: 2 }}>
                {selectedRow.title || selectedRow.questionId}
              </Typography>

              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                {selectedRow.section && (
                  <Chip
                    label={selectedRow.section}
                    size="small"
                    color="primary"
                  />
                )}
                {getStatusChip(selectedRow.answer)}
                <Box sx={{ flex: 1 }} />
                {selectedAuditDetails?.functionPath && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={runAudit}
                    disabled={isRunningAudit || isLoadingDetails}
                    sx={{ minWidth: 120 }}
                  >
                    {isRunningAudit ? (
                      <>
                        <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                        Running...
                      </>
                    ) : (
                      'Run Audit'
                    )}
                  </Button>
                )}
              </Stack>
              {selectedAuditDetails?.jobId && (
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`Job ID: ${selectedAuditDetails.jobId}`}
                    size="small"
                    variant="outlined"
                    color="info"
                  />
                </Box>
              )}
            </Box>

            {/* Loading indicator */}
            {isLoadingDetails && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading job details...
                </Typography>
              </Box>
            )}

            {/* Results Section - Structured Display */}
            {!isLoadingDetails && selectedAuditDetails && (selectedAuditDetails.question || selectedAuditDetails.title || selectedAuditDetails.answer) ? (
              <>
                {/* Question Header */}
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  }}
                >
                  {selectedAuditDetails.section && (
                    <Chip
                      label={selectedAuditDetails.section}
                      size="small"
                      sx={{ mb: 2, fontWeight: 600 }}
                      color="primary"
                    />
                  )}
                  <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                    {selectedAuditDetails.title || 'Audit Result'}
                  </Typography>
                  {selectedAuditDetails.question && (
                    <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      "{selectedAuditDetails.question}"
                    </Typography>
                  )}
                </Paper>

                {/* Answer Status */}
                {selectedAuditDetails.answer && (
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: 2,
                      bgcolor:
                        selectedAuditDetails.answer.toLowerCase() === 'pass'
                          ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.2 : 0.1)
                          : selectedAuditDetails.answer.toLowerCase() === 'fail'
                          ? alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.2 : 0.1)
                          : alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
                      border: `2px solid ${
                        selectedAuditDetails.answer.toLowerCase() === 'pass'
                          ? theme.palette.success.main
                          : selectedAuditDetails.answer.toLowerCase() === 'fail'
                          ? theme.palette.error.main
                          : theme.palette.warning.main
                      }`,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box>
                        {selectedAuditDetails.answer.toLowerCase() === 'pass' ? (
                          <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main' }} />
                        ) : selectedAuditDetails.answer.toLowerCase() === 'fail' ? (
                          <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
                        ) : (
                          <WarningIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                        )}
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Status: {selectedAuditDetails.answer}
                        </Typography>
                        {selectedAuditDetails.ai?.confidence !== undefined && (
                          <Typography variant="body2" color="text.secondary">
                            Confidence: {selectedAuditDetails.ai.confidence}%
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                )}

                {/* Comments/Findings */}
                {(selectedAuditDetails.ai?.reviewerPerspective || selectedAuditDetails.ai?.auditorPerspective || selectedAuditDetails.comments) && (
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.background.paper, 0.8),
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Findings
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {/* General Comments */}
                      {selectedAuditDetails.comments && (
                        <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedAuditDetails.comments}
                        </Typography>
                      )}

                      {/* Reviewer Perspective */}
                      {selectedAuditDetails.ai?.reviewerPerspective && (
                        <Paper
                          sx={{
                            p: 2,
                            mb: 2,
                            bgcolor: alpha(theme.palette.info.main, 0.08),
                            borderLeft: `4px solid ${theme.palette.info.main}`,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold" color="text.primary" gutterBottom>
                            👤 Reviewer Perspective
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {selectedAuditDetails.ai.reviewerPerspective}
                          </Typography>
                        </Paper>
                      )}

                      {/* Auditor Perspective */}
                      {selectedAuditDetails.ai?.auditorPerspective && (
                        <Paper
                          sx={{
                            p: 2,
                            mb: 2,
                            bgcolor: alpha(theme.palette.warning.main, 0.08),
                            borderLeft: `4px solid ${theme.palette.warning.main}`,
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold" color="text.primary" gutterBottom>
                            🔍 Auditor Perspective
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {selectedAuditDetails.ai.auditorPerspective}
                          </Typography>
                        </Paper>
                      )}

                      {/* Auditor Override */}
                      <Accordion
                        sx={{
                          mb: 2,
                          bgcolor: alpha(theme.palette.info.main, 0.05),
                          '&:before': { display: 'none' },
                          boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.1)}`,
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            bgcolor: alpha(theme.palette.info.main, 0.08),
                            '&:hover': {
                              bgcolor: alpha(theme.palette.info.main, 0.12),
                            },
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                            ✏️ Auditor Override
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2 }}>
                          <Stack spacing={2}>
                            <Typography variant="caption" color="text.secondary">
                              Add custom instructions that will be appended to the AI prompt when processing this audit.
                            </Typography>
                            {selectedAuditDetails.ai?.userPrompt && (
                              <Paper
                                sx={{
                                  p: 1.5,
                                  bgcolor: alpha(theme.palette.success.main, 0.05),
                                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                }}
                              >
                                <Typography variant="caption" fontWeight="bold" color="success.main">
                                  Current Override:
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                  {selectedAuditDetails.ai.userPrompt}
                                </Typography>
                              </Paper>
                            )}
                            <TextField
                              multiline
                              rows={4}
                              fullWidth
                              variant="outlined"
                              placeholder="Enter custom instructions for the AI prompt..."
                              value={auditorOverride}
                              onChange={(e) => setAuditorOverride(e.target.value)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                                },
                              }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={handleSaveOverride}
                                disabled={isSavingOverride || !auditorOverride.trim()}
                                startIcon={isSavingOverride ? <CircularProgress size={16} /> : <SaveIcon />}
                                sx={{
                                  background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                                  '&:hover': {
                                    background: `linear-gradient(135deg, ${theme.palette.info.dark} 0%, ${theme.palette.info.darker || theme.palette.info.dark} 100%)`,
                                  },
                                }}
                              >
                                {isSavingOverride ? 'Saving...' : 'Save Override'}
                              </Button>
                            </Box>
                          </Stack>
                        </AccordionDetails>
                      </Accordion>

                      {/* Account Profile — persistent customer context that
                          gets injected into matching audits' AI prompts. The
                          per-audit Auditor Override (above) is for this run
                          only; this is for facts that should persist. */}
                      {selectedAuditDetails.account && selectedAuditDetails.functionPath && (
                        <AccountProfileEditor
                          account={selectedAuditDetails.account}
                          customer={selectedAuditDetails.account_name || selectedAuditDetails.customer}
                          functionPath={selectedAuditDetails.functionPath}
                        />
                      )}
                    </Box>
                  </Paper>
                )}

                {/* Detailed Findings - Collapsible */}
                {selectedAuditDetails.ai?.detailedFindings && (
                  <Accordion
                    defaultExpanded={false}
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      '&:before': { display: 'none' },
                      boxShadow: theme.palette.mode === 'dark'
                        ? `0 2px 8px ${alpha(theme.palette.common.black, 0.3)}`
                        : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                        },
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        📋 Detailed Findings
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        p: 3,
                        bgcolor: alpha(theme.palette.background.paper, 0.8),
                      }}
                    >
                      <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                        {selectedAuditDetails.ai.detailedFindings.split('\n').map((line, idx) => {
                          // Check for status-related patterns
                          const statusMatch = line.match(/(Status|Result|Outcome|Finding|Compliance):\s*(Pass|Passed|Fail|Failed|Warning|Warn|Error|Success|Compliant|Non-Compliant|Not Compliant|Alert|Critical|Info|Information|OK|N\/A|Pending)/i);

                          if (statusMatch) {
                            const [fullMatch, label, status] = statusMatch;
                            const statusLower = status.toLowerCase();

                            let statusColor = theme.palette.text.primary;
                            let bgColor = 'transparent';

                            if (statusLower.includes('pass') || statusLower.includes('success') || statusLower.includes('compliant') || statusLower === 'ok') {
                              statusColor = theme.palette.success.main;
                              bgColor = alpha(theme.palette.success.main, 0.15);
                            } else if (statusLower.includes('fail') || statusLower.includes('error') || statusLower.includes('critical') || statusLower.includes('non-compliant') || statusLower.includes('not compliant')) {
                              statusColor = theme.palette.error.main;
                              bgColor = alpha(theme.palette.error.main, 0.15);
                            } else if (statusLower.includes('warning') || statusLower.includes('warn') || statusLower.includes('alert') || statusLower.includes('pending')) {
                              statusColor = theme.palette.warning.main;
                              bgColor = alpha(theme.palette.warning.main, 0.15);
                            } else if (statusLower.includes('info') || statusLower === 'n/a') {
                              statusColor = theme.palette.info.main;
                              bgColor = alpha(theme.palette.info.main, 0.15);
                            }

                            const parts = line.split(fullMatch);
                            return (
                              <Box key={idx} sx={{ display: 'block', mb: 0.5 }}>
                                {parts[0]}{label}:{' '}
                                <Box
                                  component="span"
                                  sx={{
                                    color: statusColor,
                                    bgcolor: bgColor,
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontWeight: 'bold',
                                  }}
                                >
                                  {status}
                                </Box>
                                {parts[1]}
                              </Box>
                            );
                          }

                          return <Box key={idx} sx={{ display: 'block', mb: 0.5 }}>{line || '\u00A0'}</Box>;
                        })}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Remediation */}
                {(selectedAuditDetails.ai?.remediationType || selectedAuditDetails.ai?.remediationDetails) && (
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                      border: `2px solid ${theme.palette.warning.main}`,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="warning.main">
                      🔧 Remediation Required
                    </Typography>
                    {selectedAuditDetails.ai?.remediationType && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Type:
                        </Typography>
                        <Chip
                          label={selectedAuditDetails.ai.remediationType}
                          size="small"
                          color="warning"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    )}
                    {selectedAuditDetails.ai?.remediationDetails && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Details:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {selectedAuditDetails.ai.remediationDetails}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                )}

                {/* Additional Metadata */}
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                  }}
                >
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    {selectedAuditDetails.questionId && (
                      <Chip
                        label={`ID: ${selectedAuditDetails.questionId}`}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          const url = `/audit/runbook?filePath=${encodeURIComponent(selectedAuditDetails.questionId)}`;
                          window.open(url, '_blank');
                        }}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            borderColor: theme.palette.primary.main,
                          },
                        }}
                      />
                    )}
                    {selectedAuditDetails.ai?.needsHumanReview !== undefined && (
                      <Chip
                        label={
                          selectedAuditDetails.ai.needsHumanReview
                            ? 'Needs Human Review'
                            : 'No Review Required'
                        }
                        size="small"
                        color={selectedAuditDetails.ai.needsHumanReview ? 'warning' : 'success'}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  {selectedAuditDetails.ai?.reasoningTrace && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Reasoning Trace:
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        {selectedAuditDetails.ai.reasoningTrace}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </>
            ) : !isLoadingDetails && selectedAuditDetails ? (
              /* Fallback: Show raw data if structure doesn't match */
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  mb: 4,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                  border: `2px solid ${theme.palette.warning.main}`,
                }}
              >
                <Typography variant="h6" color="warning.main" gutterBottom>
                  Raw Data (Unstructured Format)
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                    maxHeight: '600px',
                    overflow: 'auto',
                  }}
                >
                  <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(selectedAuditDetails, null, 2)}
                  </pre>
                </Paper>
              </Paper>
            ) : !isLoadingDetails && !selectedAuditDetails ? (
              <Paper
                sx={{
                  p: 3,
                  mb: 4,
                  textAlign: 'center',
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  No audit data available.
                </Typography>
              </Paper>
            ) : null}

            {/* Additional metadata if present */}
            {!isLoadingDetails && selectedAuditDetails && (selectedAuditDetails.auditRunId || selectedAuditDetails.auditType || selectedAuditDetails.account) && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Audit Metadata
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 2,
                  }}
                >
                  <Stack spacing={1}>
                    {selectedAuditDetails.auditRunId && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">
                          Audit Run ID:
                        </Typography>
                        <Typography variant="body2">{selectedAuditDetails.auditRunId}</Typography>
                      </Box>
                    )}
                    {selectedAuditDetails.auditType && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">
                          Audit Type:
                        </Typography>
                        <Typography variant="body2">{selectedAuditDetails.auditType}</Typography>
                      </Box>
                    )}
                    {selectedAuditDetails.account && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">
                          Account:
                        </Typography>
                        <Typography variant="body2">{selectedAuditDetails.account}</Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Index;
